if (!customElements.get('featured-product-cards')) {
  class FeaturedProductCards extends HTMLElement {
    constructor() {
      super();
      this.inner = null;
      this.cards = [];
      this.controls = null;
      this.progressBar = null;
      this.prevButton = null;
      this.nextButton = null;
      this.animationsEnabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.touchActionsMediaQuery = window.matchMedia('(max-width: 1067px), (hover: none)');
      this.minimumCardWidth = Number(this.dataset.minCardWidth || 520);
      this.sliderMaxBreakpoint = Number(this.dataset.sliderMaxBreakpoint || 0);
      this.resizeObserver = null;
      this.swiper = null;
      this.onCardActionClick = this.onCardActionClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.syncActionsState = this.syncActionsState.bind(this);
      this.resolveControls = this.resolveControls.bind(this);
      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.syncSliderMode = this.syncSliderMode.bind(this);
      this.setActionsOpen = this.setActionsOpen.bind(this);
    }

    connectedCallback() {
      this.inner = this.querySelector('.featured-product-cards__inner');
      this.cards = Array.from(this.querySelectorAll('[data-featured-product-card]'));
      this.resolveControls();

      this.cards.forEach((card) => {
        const primary = card.querySelector('[data-primary-image]');
        const secondary = card.querySelector('[data-secondary-image]');

        if (!primary || !secondary) {
          return;
        }

        card.classList.add('featured-product-card--has-secondary');

        if (this.animationsEnabled && this.canHover) {
          card.classList.add('featured-product-card--animated');

          card.featuredProductCardLayers = { primary, secondary };

          gsap.set(primary, {
            autoAlpha: 1,
            scale: 1,
            force3D: true
          });
          gsap.set(secondary, {
            autoAlpha: 0,
            scale: 1.015,
            force3D: true
          });

          card.addEventListener('pointerenter', this.onEnter);
          card.addEventListener('pointerleave', this.onLeave);
        }

        card.addEventListener('focusin', this.onEnter);
        card.addEventListener('focusout', this.onLeave);
      });

      this.addEventListener('click', this.onCardActionClick);
      document.addEventListener('click', this.onDocumentClick);

      if (this.touchActionsMediaQuery.addEventListener) {
        this.touchActionsMediaQuery.addEventListener('change', this.syncActionsState);
      } else if (this.touchActionsMediaQuery.addListener) {
        this.touchActionsMediaQuery.addListener(this.syncActionsState);
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.syncSliderMode();
      });

      this.resizeObserver.observe(this);
      this.syncSliderMode();
      window.addEventListener('load', this.syncSliderMode, { once: true });
      window.requestAnimationFrame(() => {
        this.resolveControls();
        this.syncSliderMode();
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onCardActionClick);
      document.removeEventListener('click', this.onDocumentClick);

      if (this.touchActionsMediaQuery.removeEventListener) {
        this.touchActionsMediaQuery.removeEventListener('change', this.syncActionsState);
      } else if (this.touchActionsMediaQuery.removeListener) {
        this.touchActionsMediaQuery.removeListener(this.syncActionsState);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.destroySwiper();
    }

    onCardActionClick(event) {
      const toggle = event.target.closest('.featured-product-card__actions-toggle');

      if (toggle) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.touchActionsMediaQuery.matches) {
          return;
        }

        const card = toggle.closest('[data-featured-product-card]');

        if (!card) {
          return;
        }

        const isOpen = !card.classList.contains('featured-product-card--actions-open');

        this.cards.forEach((existingCard) => {
          if (existingCard !== card) {
            this.setActionsOpen(existingCard, false);
          }
        });

        this.setActionsOpen(card, isOpen);
        return;
      }

      const button = event.target.closest('.featured-product-card__btn');

      if (!button) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
        return;
      }

      const variantId = button.dataset.variantId;

      if (!variantId) {
        return;
      }

      if (button.classList.contains('featured-product-card__btn--buy')) {
        this.redirectToCheckout(button, variantId);
        return;
      }

      if (button.classList.contains('featured-product-card__btn--cart')) {
        this.addToCart(button, variantId);
      }
    }

    onDocumentClick(event) {
      if (!this.touchActionsMediaQuery.matches) {
        return;
      }

      this.cards.forEach((card) => {
        if (!card.classList.contains('featured-product-card--actions-open')) {
          return;
        }

        const actions = card.querySelector('.featured-product-card__actions');
        const toggle = card.querySelector('.featured-product-card__actions-toggle');
        const clickedInsideActions = actions && actions.contains(event.target);
        const clickedToggle = toggle && toggle.contains(event.target);

        if (clickedInsideActions || clickedToggle) {
          return;
        }

        this.setActionsOpen(card, false);
      });
    }

    syncActionsState() {
      if (this.touchActionsMediaQuery.matches) {
        return;
      }

      this.cards.forEach((card) => {
        this.setActionsOpen(card, false);
      });
    }

    setActionsOpen(card, isOpen) {
      if (!card) {
        return;
      }

      const actions = card.querySelector('.featured-product-card__actions');
      const toggle = card.querySelector('.featured-product-card__actions-toggle');

      card.classList.toggle('featured-product-card--actions-open', isOpen);

      if (toggle) {
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      if (!actions) {
        return;
      }

      if (!this.touchActionsMediaQuery.matches) {
        actions.style.removeProperty('opacity');
        actions.style.removeProperty('transform');
        actions.style.removeProperty('pointer-events');
        return;
      }

      actions.style.opacity = isOpen ? '1' : '0';
      actions.style.transform = isOpen ? 'translateY(0)' : 'translateY(100%)';
      actions.style.pointerEvents = isOpen ? 'auto' : 'none';

      if (isOpen) {
        window.requestAnimationFrame(() => {
          actions.offsetHeight;
        });
      }
    }

    resolveControls() {
      const controlsScope = this.closest('.columns') || this.parentElement || this;

      this.controls = controlsScope.querySelector('.featured-product-cards__controls');
      this.progressBar = controlsScope.querySelector('.featured-product-cards__progress-bar');
      this.prevButton = controlsScope.querySelector('.featured-product-cards__arrow--prev');
      this.nextButton = controlsScope.querySelector('.featured-product-cards__arrow--next');
    }

    addToCart(button, variantId) {
      this.setButtonState(button, {
        label: 'Adding...',
        loading: true
      });

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };
      const formData = new FormData();

      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(this.getCartAddUrl(), config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            throw new Error(response.description || response.message || 'Unable to add item to cart');
          }

          this.renderCartContents(response);

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('cart:item-added', {
              product: response.hasOwnProperty('items') ? response.items[0] : response
            });
          }

          this.setButtonState(button, {
            label: 'Added!',
            loading: false
          });

          window.setTimeout(() => {
            this.resetButtonState(button);
          }, 1200);
        })
        .catch((error) => {
          console.error(error);
          this.resetButtonState(button);
        });
    }

    redirectToCheckout(button, variantId) {
      this.setButtonState(button, {
        label: 'Redirecting...',
        loading: true
      });

      const rootUrl = window.theme && theme.routes && theme.routes.root_url ? theme.routes.root_url : '/';
      const normalizedRootUrl = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;

      window.location.href = `${normalizedRootUrl}cart/${variantId}:1`;
    }

    getCartAddUrl() {
      return window.theme && theme.routes && theme.routes.cart_add_url ? theme.routes.cart_add_url : '/cart/add';
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }

    renderCartContents(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const container = document.getElementById(section.id);

        if (!container || !parsedState.sections || !parsedState.sections[section.section]) {
          return;
        }

        const elementToReplace = container.querySelector(section.selector) || container;
        const innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (innerHTML) {
          elementToReplace.innerHTML = innerHTML;
        }
      });

      if (typeof CartDrawer !== 'undefined') {
        new CartDrawer();
      }

      if (typeof Cart !== 'undefined') {
        new Cart().renderContents(parsedState);
      }

      const cartDrawer = document.getElementById('Cart-Drawer');

      if (cartDrawer) {
        document.body.classList.add('open-cc', 'open-cart');
        cartDrawer.classList.add('active');

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart-drawer:open');
        }
      }
    }

    getSectionInnerHTML(html, selector) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const target = parsedHTML.querySelector(selector);

      return target ? target.innerHTML : '';
    }

    setButtonState(button, { label, loading }) {
      if (label) {
        button.textContent = label;
      }

      if (loading) {
        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      }
    }

    resetButtonState(button) {
      this.setButtonState(button, {
        label: button.dataset.defaultText || button.textContent,
        loading: false
      });
    }

    onEnter(event) {
      const card = event.currentTarget;

      if (card.featuredProductCardLayers) {
        this.animateCard(card, true);
      } else {
        card.classList.add('is-hovered');
      }
    }

    onLeave(event) {
      const card = event.currentTarget;

      if (card.featuredProductCardLayers) {
        this.animateCard(card, false);
      } else {
        card.classList.remove('is-hovered');
      }
    }

    animateCard(card, isActive) {
      const { primary, secondary } = card.featuredProductCardLayers;

      gsap.killTweensOf([primary, secondary]);

      gsap.to(primary, {
        autoAlpha: isActive ? 0 : 1,
        scale: isActive ? 1.012 : 1,
        duration: 0.42,
        ease: 'power2.out',
        overwrite: 'auto',
        force3D: true
      });

      gsap.to(secondary, {
        autoAlpha: isActive ? 1 : 0,
        scale: isActive ? 1 : 1.015,
        duration: 0.42,
        ease: 'power2.out',
        overwrite: 'auto',
        force3D: true
      });
    }

    syncSliderMode() {
      if (!this.inner || this.cards.length < 1) {
        this.destroySwiper();
        return;
      }

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const columnsDesktop = this.getNumericCssVar('--featured-product-cards-columns', 3);
      const columnsMobile = this.getNumericCssVar('--featured-product-cards-columns-mobile', 2);
      const targetColumns = isMobile ? columnsMobile : columnsDesktop;
      const sliderWithinBreakpoint = !this.sliderMaxBreakpoint || viewportWidth <= this.sliderMaxBreakpoint;
      const gap = this.getFloatCssVar('--featured-product-card-gap', 0);
      const containerWidth = this.clientWidth;
      const gridCardWidth = targetColumns > 0
        ? Math.max((containerWidth - (Math.max(targetColumns - 1, 0) * gap)) / targetColumns, 0)
        : containerWidth;

      this.style.setProperty('--featured-product-card-min-width', `${this.minimumCardWidth}px`);
      this.style.setProperty('--featured-product-cards-active-columns', targetColumns);
      const totalContentWidth = this.cards.length * this.minimumCardWidth + (this.cards.length - 1) * gap;
      const shouldEnableSlider = sliderWithinBreakpoint && (
        totalContentWidth > containerWidth ||
        gridCardWidth < this.minimumCardWidth
      );

      if (shouldEnableSlider) {
        this.initSwiper();
      } else {
        this.destroySwiper();
      }
    }

    initSwiper() {
      if (this.swiper || typeof Swiper === 'undefined') {
        return;
      }

      this.resolveControls();

      this.classList.add('featured-product-cards--slider-active', 'swiper');
      this.inner.classList.add('swiper-wrapper');
      this.cards.forEach((card) => card.classList.add('swiper-slide'));

      this.swiper = new Swiper(this, {
        wrapperClass: 'featured-product-cards__inner',
        slideClass: 'featured-product-card',
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        spaceBetween: this.getFloatCssVar('--featured-product-card-gap', 0),
        loop: false,
        watchOverflow: true,
        threshold: 5,
        touchAngle: 50,
        longSwipes: false,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: false,
          sensitivity: 1,
          thresholdDelta: 6,
          thresholdTime: 90
        },
        touchEventsTarget: 'container',
        preventInteractionOnTransition: false,
        grabCursor: true,
        navigation: {
          prevEl: this.prevButton,
          nextEl: this.nextButton
        },
        observer: true,
        observeParents: true,
        on: {
          init: () => this.updateControls(),
          slideChange: () => this.updateControls(),
          snapIndexChange: () => this.updateControls(),
          reachBeginning: () => this.updateControls(),
          reachEnd: () => this.updateControls(),
          fromEdge: () => this.updateControls(),
          resize: () => this.updateControls()
        }
      });

      this.updateControls();
    }

    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.classList.remove('featured-product-cards--slider-active', 'swiper');
      this.inner?.classList.remove('swiper-wrapper');
      this.cards.forEach((card) => {
        card.classList.remove('swiper-slide');
        card.removeAttribute('style');
      });

      this.updateControls(true);
    }

    getNumericCssVar(name, fallbackValue) {
      const value = parseInt(getComputedStyle(this).getPropertyValue(name), 10);
      return Number.isNaN(value) ? fallbackValue : value;
    }

    getFloatCssVar(name, fallbackValue) {
      const value = parseFloat(getComputedStyle(this).getPropertyValue(name));
      return Number.isNaN(value) ? fallbackValue : value;
    }

    getPageCount() {
      if (!this.swiper) {
        return 0;
      }

      return Math.max(this.swiper.snapGrid?.length || this.swiper.slides.length || this.cards.length || 0, 1);
    }

    getActivePage(totalPages) {
      if (!this.swiper || totalPages <= 1) {
        return 0;
      }

      return Math.max(0, Math.min(this.swiper.snapIndex ?? this.swiper.activeIndex ?? 0, totalPages - 1));
    }

    updateProgress(activePage, totalPages) {
      if (!this.progressBar) {
        return;
      }

      if (totalPages <= 0) {
        this.progressBar.style.width = '0%';
        return;
      }

      this.progressBar.style.width = `${((activePage + 1) / totalPages) * 100}%`;
    }

    updateControls(isInactive = false) {
      if (!this.controls || !this.progressBar) {
        return;
      }

      if (isInactive || !this.swiper) {
        this.updateProgress(0, 0);
        this.controls.setAttribute('aria-hidden', 'true');

        if (this.prevButton) {
          this.prevButton.disabled = true;
        }

        if (this.nextButton) {
          this.nextButton.disabled = true;
        }
        return;
      }

      const totalPages = this.getPageCount();

      if (this.swiper.isLocked || totalPages <= 1) {
        this.updateProgress(0, totalPages);
        this.controls.setAttribute('aria-hidden', 'true');
        if (this.prevButton) this.prevButton.disabled = true;
        if (this.nextButton) this.nextButton.disabled = true;
        return;
      }

      this.controls.setAttribute('aria-hidden', 'false');
      const activePage = this.getActivePage(totalPages);
      this.updateProgress(activePage, totalPages);

      if (this.prevButton) {
        this.prevButton.disabled = this.swiper.isBeginning;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.swiper.isEnd;
      }
    }
  }

  customElements.define('featured-product-cards', FeaturedProductCards);
}
